import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  IconButton,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as PushIcon
} from '@mui/icons-material';
import SimplifiedNotificationSettings from './SimplifiedNotificationSettings';

const NotificationSettings = ({
  user,
  onPreferencesChange,
  isAdminEditing = false,
  disabled = false
}) => {
  // Function to determine which view to show based on user role and organization level
  const shouldUseSimplifiedNotifications = (user) => {
    if (!user) return true;
    
    // Check if user is admin
    const isAdmin = user.secondaryRoles?.admin ||
                    user.primaryRole === 'OW' ||
                    user.primaryRole === 'sOW';
    
    // Check if user is Org 1
    const isOrg1 = user.organizationLevel === 1;
    
    // Use simplified view for non-admin users OR Org 2 users
    return !isAdmin || !isOrg1;
  };
  const [preferences, setPreferences] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    channels: true,
    self: false,
    admin: false,
    system: false
  });

  // Initialize preferences from user data
  useEffect(() => {
    console.log('🔔 NotificationSettings: Initializing preferences with user data:', {
      userId: user?.id,
      hasNotificationPreferences: !!user?.notificationPreferences,
      notificationPreferences: user?.notificationPreferences,
      legacyFields: {
        receiveEmailNotifications: user?.receiveEmailNotifications,
        receiveTextNotifications: user?.receiveTextNotifications,
        receivePushNotifications: user?.receivePushNotifications
      }
    });
    
    if (user) {
      // Initialize with comprehensive notification preferences or fallback to legacy
      const initialPrefs = user.notificationPreferences || {
        channels: {
          email: { enabled: user.receiveEmailNotifications ?? true, priority: 1 },
          sms: { enabled: user.receiveTextNotifications ?? true, priority: 2 },
          push: { enabled: user.receivePushNotifications ?? true, priority: 3 }
        },
        self: {
          channels: {
            email: user.receiveEmailNotificationsForSelf ?? true,
            sms: user.receiveTextNotificationsForSelf ?? true,
            push: user.receivePushNotificationsForSelf ?? true
          },
          events: {
            newJobAssigned: {
              enabled: user.notifyOnNewJobAssignedToSelf ?? true,
              channels: { email: true, sms: true, push: true },
              urgency: 'high'
            },
            jobCanceled: {
              enabled: user.notifyOnJobCanceledForSelf ?? true,
              channels: { email: true, sms: false, push: true },
              urgency: 'medium'
            },
            jobOnScene: {
              enabled: user.notifyOnJobOnSceneForSelf ?? true,
              channels: { email: false, sms: false, push: true },
              urgency: 'low'
            },
            jobReassigned: {
              enabled: user.notifyOnJobReassignedForSelf ?? true,
              channels: { email: true, sms: true, push: true },
              urgency: 'high'
            },
            jobCompleted: {
              enabled: true,
              channels: { email: false, sms: false, push: true },
              urgency: 'low'
            },
            jobUnsuccessful: {
              enabled: true,
              channels: { email: true, sms: false, push: true },
              urgency: 'medium'
            },
            jobMarkedAsGOA: {
              enabled: user.notifyOnJobMarkedAsGOAForSelf ?? true,
              channels: { email: true, sms: false, push: true },
              urgency: 'medium'
            },
            etaApprovalRequired: {
              enabled: true,
              channels: { email: true, sms: true, push: true },
              urgency: 'high'
            },
            etaApproved: {
              enabled: true,
              channels: { email: false, sms: false, push: true },
              urgency: 'medium'
            },
            etaRejected: {
              enabled: true,
              channels: { email: true, sms: true, push: true },
              urgency: 'high'
            }
          }
        },
        admin: {
          channels: {
            email: user.adminReceiveGeneralEmailNotifications ?? true,
            sms: user.adminReceiveGeneralTextNotifications ?? true,
            push: user.adminReceiveGeneralPushNotifications ?? true
          },
          events: {
            newJob: {
              all: {
                enabled: user.adminNotifyOnNewJobAll ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              },
              forVendor: {
                enabled: user.adminNotifyOnNewJobForVendor ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              }
            },
            jobCanceled: {
              unassigned: {
                enabled: user.adminNotifyOnJobCanceledUnassigned ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              },
              forVendor: {
                enabled: user.adminNotifyOnJobCanceledForVendor ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              }
            },
            jobOnScene: {
              unassigned: {
                enabled: user.adminNotifyOnJobOnSceneUnassigned ?? false,
                channels: { email: false, sms: false, push: true },
                urgency: 'low'
              },
              forVendor: {
                enabled: user.adminNotifyOnJobOnSceneForVendor ?? false,
                channels: { email: false, sms: false, push: true },
                urgency: 'low'
              }
            },
            jobReassigned: {
              unassigned: {
                enabled: user.adminNotifyOnJobReassignedUnassigned ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              },
              forVendor: {
                enabled: user.adminNotifyOnJobReassignedForVendor ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              }
            },
            jobCompleted: {
              unassigned: {
                enabled: false,
                channels: { email: false, sms: false, push: true },
                urgency: 'low'
              },
              forVendor: {
                enabled: false,
                channels: { email: false, sms: false, push: true },
                urgency: 'low'
              }
            },
            jobMarkedAsGOA: {
              unassigned: {
                enabled: user.adminNotifyOnJobGOAUnassigned ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              },
              forVendor: {
                enabled: user.adminNotifyOnJobGOAForVendor ?? false,
                channels: { email: true, sms: false, push: true },
                urgency: 'medium'
              }
            },
            jobUnsuccessful: {
              unassigned: {
                enabled: true,
                channels: { email: true, sms: false, push: true },
                urgency: 'high'
              },
              forVendor: {
                enabled: true,
                channels: { email: true, sms: false, push: true },
                urgency: 'high'
              }
            },
            etaApprovalRequired: {
              enabled: false,
              channels: { email: true, sms: false, push: true },
              urgency: 'high'
            },
            paymentSubmitted: {
              enabled: false,
              channels: { email: true, sms: false, push: true },
              urgency: 'medium'
            },
            backgroundCheckExpiring: {
              enabled: false,
              channels: { email: true, sms: false, push: true },
              urgency: 'medium'
            },
            insuranceExpiring: {
              enabled: false,
              channels: { email: true, sms: false, push: true },
              urgency: 'medium'
            }
          }
        },
        system: {
          channels: {
            email: true,
            sms: false,
            push: true
          },
          events: {
            accountActivated: {
              enabled: true,
              channels: { email: true, sms: false, push: false }
            },
            accountDeactivated: {
              enabled: true,
              channels: { email: true, sms: false, push: false }
            },
            passwordChanged: {
              enabled: true,
              channels: { email: true, sms: true, push: false }
            },
            profileUpdated: {
              enabled: true,
              channels: { email: false, sms: false, push: true }
            }
          }
        }
      };

      console.log('🔔 NotificationSettings: Setting initial preferences:', initialPrefs);
      setPreferences(initialPrefs);
    }
  }, [user]);

  // Handle preference changes
  const handlePreferenceChange = (path, value) => {
    if (!preferences) return;

    const newPreferences = { ...preferences };
    const pathArray = path.split('.');
    let current = newPreferences;

    // Navigate to the parent of the target property
    for (let i = 0; i < pathArray.length - 1; i++) {
      if (!current[pathArray[i]]) {
        current[pathArray[i]] = {};
      }
      current = current[pathArray[i]];
    }

    // Set the value
    current[pathArray[pathArray.length - 1]] = value;

    console.log('🔔 NotificationSettings: Preference changed:', {
      path,
      value,
      newPreferences
    });
    
    setPreferences(newPreferences);
    
    // Notify parent component
    if (onPreferencesChange) {
      console.log('🔔 NotificationSettings: Calling onPreferencesChange with:', newPreferences);
      onPreferencesChange(newPreferences);
    }
  };

  // Handle section expansion
  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get channel icon
  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <EmailIcon fontSize="small" />;
      case 'sms': return <SmsIcon fontSize="small" />;
      case 'push': return <PushIcon fontSize="small" />;
      default: return null;
    }
  };

  // Get urgency color
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Render channel controls
  const renderChannelControls = (channelPath, channels, showPriority = false) => {
    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Object.entries(channels).map(([channel, config]) => (
          <Grid item xs={12} sm={4} key={channel}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getChannelIcon(channel)}
              <FormControlLabel
                control={
                  <Switch
                    checked={typeof config === 'boolean' ? config : config.enabled}
                    onChange={(e) => handlePreferenceChange(
                      `${channelPath}.${channel}${typeof config === 'boolean' ? '' : '.enabled'}`,
                      e.target.checked
                    )}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={channel.toUpperCase()}
              />
              {showPriority && typeof config === 'object' && (
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={config.priority || 1}
                    onChange={(e) => handlePreferenceChange(
                      `${channelPath}.${channel}.priority`,
                      e.target.value
                    )}
                    disabled={disabled || !config.enabled}
                  >
                    <MenuItem value={1}>High</MenuItem>
                    <MenuItem value={2}>Med</MenuItem>
                    <MenuItem value={3}>Low</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Render event controls
  const renderEventControls = (eventPath, events, category) => {
    return Object.entries(events).map(([eventKey, eventConfig]) => {
      const isNestedEvent = typeof eventConfig === 'object' && 
        !eventConfig.hasOwnProperty('enabled') && 
        !eventConfig.hasOwnProperty('channels');

      if (isNestedEvent) {
        // Handle nested events (like newJob.all, newJob.forVendor)
        return (
          <Box key={eventKey} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
              {eventKey.replace(/([A-Z])/g, ' $1').trim()}
            </Typography>
            {Object.entries(eventConfig).map(([subEventKey, subEventConfig]) => (
              <Box key={subEventKey} sx={{ ml: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={subEventConfig.enabled || false}
                        onChange={(e) => handlePreferenceChange(
                          `${eventPath}.${eventKey}.${subEventKey}.enabled`,
                          e.target.checked
                        )}
                        disabled={disabled}
                      />
                    }
                    label={subEventKey.replace(/([A-Z])/g, ' $1').trim()}
                  />
                  {subEventConfig.urgency && (
                    <Chip
                      label={subEventConfig.urgency}
                      size="small"
                      color={getUrgencyColor(subEventConfig.urgency)}
                      variant="outlined"
                    />
                  )}
                </Box>
                {subEventConfig.channels && (
                  <Box sx={{ ml: 3 }}>
                    {renderChannelControls(
                      `${eventPath}.${eventKey}.${subEventKey}.channels`,
                      subEventConfig.channels
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );
      }

      // Handle simple events
      return (
        <Box key={eventKey} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={eventConfig.enabled || false}
                  onChange={(e) => handlePreferenceChange(
                    `${eventPath}.${eventKey}.enabled`,
                    e.target.checked
                  )}
                  disabled={disabled}
                />
              }
              label={eventKey.replace(/([A-Z])/g, ' $1').trim()}
            />
            {eventConfig.urgency && (
              <Chip
                label={eventConfig.urgency}
                size="small"
                color={getUrgencyColor(eventConfig.urgency)}
                variant="outlined"
              />
            )}
          </Box>
          {eventConfig.channels && (
            <Box sx={{ ml: 3 }}>
              {renderChannelControls(`${eventPath}.${eventKey}.channels`, eventConfig.channels)}
            </Box>
          )}
        </Box>
      );
    });
  };

  if (!preferences) {
    return <Typography>Loading notification preferences...</Typography>;
  }

  const isOrg1 = user?.organizationLevel === 1;
  const hasAdminRole = user?.secondaryRoles?.admin || user?.secondaryRoles?.dispatcher;

  // Determine which view to show
  const useSimplifiedView = shouldUseSimplifiedNotifications(user);

  // Render the appropriate view based on user role and organization level
  if (useSimplifiedView) {
    return (
      <SimplifiedNotificationSettings
        user={user}
        onPreferencesChange={onPreferencesChange}
        isAdminEditing={isAdminEditing}
        disabled={disabled}
      />
    );
  }

  // Full complex view for Org 1 admin users
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Notification Preferences
        <Tooltip title="Configure how and when you receive notifications">
          <IconButton size="small" sx={{ ml: 1 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      {isAdminEditing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are editing notification preferences for {user?.firstName} {user?.lastName}
        </Alert>
      )}

      {/* Global Channel Settings */}
      <Accordion
        expanded={expandedSections.channels}
        onChange={() => handleSectionToggle('channels')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Global Channel Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure your preferred notification channels and their priorities
          </Typography>
          {renderChannelControls('channels', preferences.channels, true)}
        </AccordionDetails>
      </Accordion>

      {/* Personal Notifications */}
      <Accordion
        expanded={expandedSections.self}
        onChange={() => handleSectionToggle('self')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Personal Job Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Notifications for jobs assigned to you
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Channel Overrides</Typography>
          {renderChannelControls('self.channels', preferences.self.channels)}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 2 }}>Job Events</Typography>
          {renderEventControls('self.events', preferences.self.events, 'self')}
        </AccordionDetails>
      </Accordion>

      {/* Admin/Dispatcher Notifications */}
      {hasAdminRole && (
        <Accordion
          expanded={expandedSections.admin}
          onChange={() => handleSectionToggle('admin')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Admin/Dispatcher Notifications
              <Chip
                label={isOrg1 ? "Org Level 1" : "Org Level 2"}
                size="small"
                sx={{ ml: 1 }}
                color={isOrg1 ? "primary" : "secondary"}
              />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isOrg1
                ? "Notifications for all jobs in the system"
                : "Notifications for jobs assigned to your vendor"
              }
            </Typography>
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Channel Overrides</Typography>
            {renderChannelControls('admin.channels', preferences.admin.channels)}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Administrative Events</Typography>
            {renderEventControls('admin.events', preferences.admin.events, 'admin')}
          </AccordionDetails>
        </Accordion>
      )}

      {/* System Notifications */}
      <Accordion
        expanded={expandedSections.system}
        onChange={() => handleSectionToggle('system')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">System Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Account and system-related notifications
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Channel Overrides</Typography>
          {renderChannelControls('system.channels', preferences.system.channels)}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 2 }}>System Events</Typography>
          {renderEventControls('system.events', preferences.system.events, 'system')}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default NotificationSettings;